/**
 * insight.ts — the web side of `docs/insight-contract.md` for LabTop Finance.
 *
 * REDESIGN §2 (urgency buckets) and §3 (the written narrative) are implemented ONCE, in
 * `shared/insight`, and delivered by the server in `summary.narrative` / `summary.urgency`
 * (insight-contract §4). This module is the web's half of that contract:
 *
 *  1. `SummaryWithInsight` — `SummaryResponse` plus the two keys the contract ADDS. They are
 *     typed optional here so the bundle compiles and runs against a server that has not shipped
 *     them yet; the moment the server sends them they are used verbatim.
 *  2. `insightOf(summary)` — returns the server's payload when it is there, and otherwise builds
 *     the same two structures on the client **from the same `shared/insight` functions**, using
 *     the mapping of insight-contract §1.3 / §2.1. So the Hebrew a reader sees is byte-identical
 *     to what the server will send; only the *set of facts* is thinner (the summary payload
 *     carries no invoice rows and no debt ages), and `source` says which half produced it.
 *  3. `URGENCY_ICON` — the semantic-icon table of insight-contract §5, business column.
 *
 * Everything here is pure and clock-free: `asOf` comes from the payload, never from `new Date()`
 * (ARCHITECTURE §3). That is what makes `test/insight.test.ts` deterministic.
 */

import type { BalancePoint, DayString, ForecastEvent, SummaryResponse } from '../../api-types.ts';
import {
  buildItems,
  buildNarrative,
  countByBucket,
  groupByBucket,
} from '../../../shared/insight/src/index.ts';
import type {
  BusinessNarrativeInput,
  Narrative,
  UrgencyBucket,
  UrgencyGroup,
  UrgencyIcon,
  UrgencyInput,
  UrgencyItem,
} from '../../../shared/insight/src/index.ts';

/** insight-contract §4 — the bucketed home payload. */
export interface UrgencySummary {
  items: UrgencyItem[];
  /** Buckets with no rows are ABSENT — never an empty heading. */
  groups: UrgencyGroup[];
  counts: Record<UrgencyBucket, number>;
}

/**
 * `SummaryResponse` + the two keys insight-contract §4 adds to it. Optional only so that this
 * bundle keeps working against a server that predates the contract; `insightOf()` fills the gap.
 */
export type SummaryWithInsight = SummaryResponse & {
  narrative?: Narrative | null;
  urgency?: UrgencySummary | null;
};

/** insight-contract §5 — `UrgencyIcon` → the business `Icon.tsx` name. */
export const URGENCY_ICON: Readonly<Record<UrgencyIcon, string>> = {
  card: 'card',
  alert: 'flag',
  trend: 'activity',
  receipt: 'file',
  clock: 'clock',
  link: 'link',
  wallet: 'wallet',
  loan: 'landmark',
  tax: 'landmark',
  invoice: 'file',
  'standing-order': 'rotate',
  coverage: 'banknote',
};

/** The `CategoryIcon` glyph drawn on an urgency row, by semantic icon (REDESIGN §7b). */
export const URGENCY_GLYPH: Readonly<Record<UrgencyIcon, string>> = {
  card: 'card',
  alert: 'alert',
  trend: 'percent',
  receipt: 'receipt',
  clock: 'arrows',
  link: 'transfer',
  wallet: 'piggy',
  loan: 'loan',
  tax: 'landmark',
  invoice: 'receipt',
  'standing-order': 'exchange',
  coverage: 'banknote',
};

// ───────────────────────────── §2 · urgency inputs ─────────────────────────────

/** `ForecastEvent.kind` → the urgency kind it becomes (insight-contract §1.3, business). */
function kindOfEvent(kind: ForecastEvent['kind']): UrgencyInput['kind'] | null {
  switch (kind) {
    case 'card':
      return 'card_charge';
    case 'standing_order':
    case 'supplier':
      return 'supplier_standing_order';
    case 'invoice':
      return 'invoice_due';
    case 'tax':
      // taxes come from `tax.upcoming`, which carries the basis text — never twice.
      return null;
    default:
      return null;
  }
}

/** The first forecast day whose balance is below zero, or null (`forecast_negative`). */
export function firstNegativeDay(series: readonly BalancePoint[]): BalancePoint | null {
  for (const point of series) if (!point.actual && point.balanceAgorot < 0) return point;
  return null;
}

/**
 * The `UrgencyInput[]` the business server owes (insight-contract §1.3). Built here from the
 * summary payload so the screen works before the server ships `summary.urgency`.
 *
 * What the summary payload cannot supply, and the server therefore does better: one dated row per
 * overdue invoice (here they collapse into a single pinned row) and `invoice_due` rows beyond the
 * 30-day forecast window.
 */
export function businessUrgencyInputs(summary: SummaryResponse, labels: UrgencyLabels): UrgencyInput[] {
  const inputs: UrgencyInput[] = [];
  const { tax, balance, collection, connections, freshness } = summary;

  // VAT report + payment (one deadline, one row — the sentence names both).
  if (tax.period && tax.dueDate) {
    inputs.push({
      id: `vat:${tax.period.id}`,
      kind: 'vat_report',
      date: tax.dueDate,
      title: labels.vatReport(tax.period.label),
      amountAgorot: -Math.abs(tax.netVatAgorot),
      actionHref: labels.vatHref(tax.period.id),
    });
  }

  // Income-tax advances and national insurance.
  for (const event of tax.upcoming) {
    if (event.paidAt) continue;
    if (event.type === 'vat') continue;
    inputs.push({
      id: `tax:${event.id}`,
      kind: event.type === 'national_insurance' ? 'national_insurance' : 'tax_advance',
      date: event.dueDate,
      title: event.label,
      amountAgorot: -Math.abs(event.amountAgorot),
      actionHref: labels.taxHref,
    });
  }

  // Everything the 30-day forecast already dates: the card charge, supplier standing orders,
  // invoices expected to be collected.
  for (const event of balance.events) {
    const kind = kindOfEvent(event.kind);
    if (!kind) continue;
    inputs.push({
      id: `ev:${event.kind}:${event.day}:${event.amountAgorot}`,
      kind,
      date: event.day,
      title: event.label,
      amountAgorot: event.amountAgorot,
      actionHref: kind === 'invoice_due' ? labels.invoicesHref : labels.cashflowHref,
    });
  }

  // Money already late. The summary carries the totals, not the rows, so this is one row.
  if (collection.overdueCount > 0) {
    inputs.push({
      id: 'collection:overdue',
      kind: 'invoice_overdue',
      date: null,
      pin: 'now',
      title: labels.invoicesOverdue(collection.overdueCount),
      amountAgorot: collection.overdueAgorot,
      actionHref: labels.overdueHref,
    });
  }

  // Expenses with no tax invoice — a trend, never a deadline (§2 `later`).
  if (tax.nonDeductibleCount > 0) {
    inputs.push({
      id: 'expense:no-invoice',
      kind: 'expense_no_invoice',
      date: null,
      title: labels.noInvoice(tax.nonDeductibleCount),
      amountAgorot: -Math.abs(tax.nonDeductibleVatAgorot),
      actionHref: labels.noInvoiceHref,
    });
  }

  // The forecast crossing zero.
  const negative = firstNegativeDay(balance.series);
  if (negative) {
    inputs.push({
      id: 'forecast:negative',
      kind: 'forecast_negative',
      date: negative.day,
      title: labels.forecastNegative,
      amountAgorot: negative.balanceAgorot,
      actionHref: labels.cashflowHref,
    });
  }

  // Connections. A lapsed consent is `later` while the data is still fresh (REDESIGN §8) and
  // `now` the moment nothing is left to refresh with.
  const blocking = !connections.some((connection) => connection.status === 'ACTIVE');
  for (const connection of connections) {
    if (!connection.needsRelink) continue;
    inputs.push({
      id: `conn:${connection.id}`,
      kind: 'connection_expired',
      date: connection.expiryDate,
      title: labels.connectionExpired(connection.providerId),
      providerId: connection.providerId,
      actionHref: labels.connectionsHref,
      ...(blocking ? { pin: 'now' as const } : {}),
    });
  }

  if (freshness.level === 'stale_warn' || freshness.level === 'stale_critical' || freshness.level === 'none') {
    inputs.push({
      id: `stale:${freshness.asOf}`,
      kind: 'data_stale',
      date: null,
      title: labels.dataStale,
      actionHref: labels.connectionsHref,
    });
  }

  return inputs;
}

/** The Hebrew and the routes the inputs need — supplied by the caller so `i18n/he.ts` stays the one place copy lives. */
export interface UrgencyLabels {
  vatReport: (period: string) => string;
  vatHref: (period: string) => string;
  taxHref: string;
  cashflowHref: string;
  invoicesHref: string;
  overdueHref: string;
  invoicesOverdue: (n: number) => string;
  noInvoice: (n: number) => string;
  noInvoiceHref: string;
  forecastNegative: string;
  connectionExpired: (provider: string) => string;
  connectionsHref: string;
  dataStale: string;
}

// ───────────────────────────── §3 · narrative input ─────────────────────────────

/**
 * The `BusinessNarrativeInput` of insight-contract §2.1, as far as the summary payload reaches.
 *
 * `collection.topDebtor` and `missingInvoices` are deliberately `null`: the age of the largest
 * debt and the gross of the invoice-less expenses are not in this payload, and §3 says to DROP a
 * sentence whose input is missing rather than hedge it. The server, which has the rows, fills
 * both in.
 */
export function businessNarrativeInput(summary: SummaryResponse): BusinessNarrativeInput {
  const { month, collection, tax, balance, state } = summary;
  const hasMonth = state !== 'no_data';
  return {
    system: 'business',
    month: hasMonth
      ? { inAgorot: month.incomeNetAgorot, outAgorot: month.expenseNetAgorot, profitAgorot: month.profitAgorot }
      : null,
    collection:
      collection.openAgorot > 0
        ? { openAgorot: collection.openAgorot, overdueAgorot: collection.overdueAgorot, topDebtor: null }
        : null,
    vat:
      tax.period && tax.dueDate
        ? { periodLabel: tax.period.label, dueDate: tax.dueDate, amountAgorot: tax.netVatAgorot }
        : null,
    runway: balance.forecastAgorot === null ? null : { forecastAgorot: balance.forecastAgorot },
    missingInvoices: null,
  };
}

// ───────────────────────────── the one entry point ─────────────────────────────

export interface Insight {
  narrative: Narrative;
  urgency: UrgencySummary;
  /** `server` = delivered in the payload · `client` = built here from the same shared code. */
  source: 'server' | 'client';
}

/** `freshness.asOf` — the day every metric already uses. Never `todayIL()` (ARCHITECTURE §3). */
export function asOfOf(summary: SummaryResponse): DayString {
  return summary.freshness.asOf || summary.asOf;
}

/**
 * The names of the two fields `GET /api/summary` must carry (REDESIGN §3/§9, insight-contract §1).
 * A payload missing either one is a BUG, not a mode: the reconstruction below is a last-resort
 * resilience path so a partial payload does not blank the home screen, and it is deliberately
 * loud — it logs, and `source: 'client'` puts a visible notice on the screen — because a silent
 * fallback is how a preview came to render a second, divergent product nobody was shipping.
 */
export function insightOf(summary: SummaryWithInsight, labels: UrgencyLabels): Insight {
  const asOf = asOfOf(summary);
  if (summary.narrative && summary.urgency) {
    return { narrative: summary.narrative, urgency: summary.urgency, source: 'server' };
  }
  const missing = [summary.narrative ? null : 'summary.narrative', summary.urgency ? null : 'summary.urgency'].filter(Boolean).join(', ');
  console.error(
    `[insight] the API payload is missing ${missing}. REDESIGN §3 requires the server to compute both; ` +
      'the screen is now showing a client-side reconstruction, which is NOT what ships.',
  );
  const items = summary.urgency?.items ?? buildItems(businessUrgencyInputs(summary, labels), asOf);
  return {
    narrative: summary.narrative ?? buildNarrative(businessNarrativeInput(summary), asOf),
    urgency: summary.urgency ?? { items, groups: groupByBucket(items), counts: countByBucket(items) },
    source: 'client',
  };
}
